import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Field } from '../ui/Field.tsx';
import { describeError } from '../ui/problemDetails.ts';
import { register, resendConfirmationEmail } from './authApi.ts';

export function RegisterPage() {
  const [searchParams] = useSearchParams();
  // Present when someone arrived from a "join my household" email. Carried through
  // registration so the invitation is waiting for them once their address is confirmed.
  const invitationToken = searchParams.get('invitation') ?? undefined;

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [resent, setResent] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await register({
        email: email.trim(),
        displayName: displayName.trim(),
        password,
        ...(invitationToken ? { invitationToken } : {}),
      });
      setRegistered(true);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResend() {
    try {
      await resendConfirmationEmail(email.trim());
      setResent(true);
    } catch {
      // Deliberately quiet: this endpoint reports success regardless, and an error here
      // would only tell someone whether an address is registered.
      setResent(true);
    }
  }

  if (registered) {
    return (
      <main className="app-shell app-shell--centred">
        <section className="card stack">
          <h2>Check your email</h2>
          <p>
            We&rsquo;ve sent a confirmation link to <strong>{email}</strong>. Open it and you&rsquo;re
            in.
          </p>
          <p className="muted">
            {invitationToken
              ? 'Your invitation will be waiting for you afterwards — you can accept or decline it then.'
              : 'The link is valid for 24 hours.'}
          </p>

          <button
            className="button button--quiet"
            type="button"
            onClick={() => void handleResend()}
            disabled={resent}
          >
            {resent ? 'Sent again' : 'Send it again'}
          </button>

          <p className="muted centred">
            <Link to="/login">Back to sign in</Link>
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="app-shell app-shell--centred">
      <header className="auth-header">
        <h1>Listocial</h1>
        <p className="muted">
          {invitationToken
            ? 'Create an account to join the list you were invited to.'
            : 'Shared shopping lists that update live.'}
        </p>
      </header>

      <form className="card stack" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <h2>Create an account</h2>

        <Field
          label="Your name"
          name="displayName"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          hint="Shown next to the things you tick off."
          autoComplete="name"
          maxLength={80}
          required
        />

        <Field
          label="Email"
          type="email"
          name="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          required
        />

        <Field
          label="Password"
          type="password"
          name="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint="At least 10 characters. A few words you'll remember beats something clever."
          autoComplete="new-password"
          minLength={10}
          required
        />

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <button className="button button--primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creating…' : 'Create account'}
        </button>

        <p className="muted centred">
          Already have one? <Link to="/login">Sign in</Link>
        </p>
      </form>
    </main>
  );
}
